import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, BookOpen, RotateCw, Sparkles, Check } from "lucide-react";

const notes = [
  { gs: "GS-2", tag: "Polity", title: "Basic Structure Doctrine", line: "Kesavananda Bharati case — Parliament cannot alter the Constitution's basic structure.", saved: "From 6 briefs" },
  { gs: "GS-3", tag: "Economy", title: "Repo vs Reverse Repo", line: "Repo: rate at which RBI lends to banks. Reverse repo: rate RBI pays banks to park funds.", saved: "From 4 briefs" },
  { gs: "GS-1", tag: "Geography", title: "El Niño & the monsoon", line: "Pacific warming weakens the Indian monsoon; historically linked to drought years.", saved: "From 3 briefs" },
  { gs: "GS-3", tag: "Environment", title: "Green Hydrogen Mission", line: "Targets 5 MMT annual production by 2030; key for net-zero 2070 commitment.", saved: "From 5 briefs" },
];

const filters = ["All", "GS-1", "GS-2", "GS-3"];

const perks = [
  "Every brief auto-tagged to your exam syllabus",
  "One-tap save with key takeaways extracted",
  "Auto-generated flashcards for spaced revision",
  "Monthly current-affairs digests as quizzes",
];

const StudentNotebook = () => {
  const [filter, setFilter] = useState("All");
  const [flipped, setFlipped] = useState(false);

  const visible = filter === "All" ? notes : notes.filter((n) => n.gs === filter);

  return (
    <section id="students" data-testid="students-section" className="py-20 lg:py-32 border-t border-[#1E293B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <p className="mono-label">Student Notebook</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-4">
            Current affairs, converted into marks.
          </h2>
          <p className="text-slate-400 mt-4 leading-relaxed">
            Stop maintaining messy newspaper cuttings. InNewZ turns daily briefs into a
            structured, syllabus-mapped notebook for UPSC, SSC, Banking, and GRE aspirants.
          </p>
          <ul className="mt-7 space-y-3.5">
            {perks.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-slate-300">
                <span className="mt-0.5 h-5 w-5 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
                  <Check className="h-3 w-3 text-emerald-400" />
                </span>
                {p}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex items-center gap-3 rounded-xl border border-amber-500/25 bg-amber-500/5 p-4">
            <GraduationCap className="h-6 w-6 text-amber-400 shrink-0" />
            <p className="text-sm text-slate-300">
              Built with inputs from UPSC toppers and coaching faculty.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7 space-y-6">
          <div className="rounded-2xl border border-[#1E293B] bg-gradient-to-b from-slate-800/60 to-slate-900/80 p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2.5">
                <BookOpen className="h-5 w-5 text-cyan-400" />
                <span className="font-display font-semibold">My Notebook</span>
              </div>
              <div className="flex gap-1.5">
                {filters.map((f) => (
                  <button
                    key={f}
                    data-testid={`gs-filter-${f.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                    onClick={() => setFilter(f)}
                    className={`rounded-full border px-3 py-1 text-[11px] font-mono-x transition-colors duration-200 ${
                      filter === f
                        ? "border-cyan-400 bg-cyan-500/15 text-cyan-300"
                        : "border-slate-700 text-slate-500 hover:text-slate-300"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 space-y-3" data-testid="notebook-notes-list">
              <AnimatePresence mode="popLayout">
                {visible.map((n) => (
                  <motion.div
                    key={n.title}
                    layout
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.25 }}
                    className="rounded-xl border border-[#1E293B] bg-slate-900/70 p-4 flex items-start gap-4"
                  >
                    <span className="rounded-md border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 text-[10px] font-mono-x text-cyan-300 shrink-0">
                      {n.gs}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-100">{n.title}</p>
                        <span className="text-[10px] font-mono-x uppercase tracking-widest text-amber-400">{n.tag}</span>
                      </div>
                      <p className="text-xs text-slate-400 leading-relaxed mt-1">{n.line}</p>
                      <p className="text-[10px] font-mono-x uppercase tracking-widest text-slate-600 mt-2">{n.saved}</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          <div
            data-testid="flashcard"
            onClick={() => setFlipped((f) => !f)}
            className="cursor-pointer [perspective:1200px]"
          >
            <motion.div
              animate={{ rotateY: flipped ? 180 : 0 }}
              transition={{ duration: 0.5 }}
              className="relative h-44 [transform-style:preserve-3d]"
            >
              <div className="absolute inset-0 rounded-2xl border border-[#1E293B] bg-gradient-to-br from-cyan-500/10 to-slate-900 p-6 flex flex-col justify-between [backface-visibility:hidden]">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono-x uppercase tracking-widest text-cyan-400">Flashcard • Polity</span>
                  <Sparkles className="h-4 w-4 text-cyan-400" />
                </div>
                <p className="font-display text-lg font-semibold">What does Article 370 of the Constitution relate to?</p>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-x uppercase tracking-widest text-slate-500">
                  <RotateCw className="h-3 w-3" /> Tap to flip
                </span>
              </div>
              <div className="absolute inset-0 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-slate-900 p-6 flex flex-col justify-between [backface-visibility:hidden] [transform:rotateY(180deg)]">
                <span className="text-[10px] font-mono-x uppercase tracking-widest text-emerald-400">Answer</span>
                <p className="text-sm text-slate-200 leading-relaxed">
                  Special autonomous status of Jammu & Kashmir. Abrogated in August 2019; the state was
                  reorganised into two Union Territories.
                </p>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-x uppercase tracking-widest text-slate-500">
                  <RotateCw className="h-3 w-3" /> Tap to flip back
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StudentNotebook;
